import { useEffect, useState } from "react";
import {
  Body1,
  Caption1,
  Spinner,
  Subtitle2,
  makeStyles,
  mergeClasses,
  tokens
} from "@fluentui/react-components";
import { fetchWithRetry } from "../lib/fetchWithRetry";

/**
 * BalancePanel — compact strip of current fiscal-year balance tiles, one per
 * leave type that counts against balance and isn't unlimited. Shown above the
 * requests table on /requests so users can see at-a-glance how many days they
 * have left before submitting a new request.
 *
 * Data source: GET /api/leave-balances (admin can pass ?userId= to view other).
 */

interface BalanceRow {
  leaveTypeId: string;
  leaveTypeName: string;
  sortOrder: number;
  accrued: number;
  used: number;
  pending: number;
  remaining: number;
}

interface BalancesResponse {
  userId: string;
  userDisplayName: string;
  fiscalYear: number;
  balances: BalanceRow[];
}

const useStyles = makeStyles({
  root: {
    marginTop: tokens.spacingVerticalS,
    marginBottom: tokens.spacingVerticalL
  },
  header: {
    display: "flex",
    alignItems: "baseline",
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalS
  },
  tiles: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM
  },
  tile: {
    flex: "1 1 180px",
    minWidth: "180px",
    maxWidth: "260px",
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS
  },
  typeName: {
    color: tokens.colorNeutralForeground2
  },
  remaining: {
    fontSize: tokens.fontSizeHero700,
    lineHeight: tokens.lineHeightHero700,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1
  },
  remainingNegative: {
    color: tokens.colorPaletteRedForeground1
  },
  unit: {
    color: tokens.colorNeutralForeground3,
    marginLeft: tokens.spacingHorizontalXXS,
    fontSize: tokens.fontSizeBase300
  },
  breakdown: {
    color: tokens.colorNeutralForeground3
  },
  error: {
    color: tokens.colorPaletteRedForeground1
  }
});

const numberFmt = new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 });
function formatDays(n: number): string {
  return numberFmt.format(n);
}

interface BalancePanelProps {
  userIdParam: string | null;
  // Bump from the parent after a create/cancel mutation to force a re-fetch.
  version?: number;
}

export default function BalancePanel({ userIdParam, version = 0 }: BalancePanelProps) {
  const styles = useStyles();
  const [data, setData] = useState<BalancesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warming, setWarming] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    setWarming(false);

    const qs = userIdParam ? `?userId=${encodeURIComponent(userIdParam)}` : "";
    fetchWithRetry(`/api/leave-balances${qs}`, undefined, { onWarming: () => !cancelled && setWarming(true) })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return (await r.json()) as BalancesResponse;
      })
      .then((d) => {
        if (cancelled) return;
        setData(d);
        setWarming(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
      });

    return () => { cancelled = true; };
  }, [userIdParam, version]);

  if (error) {
    return (
      <div className={styles.root}>
        <Body1 as="p" className={styles.error}>Couldn't load balances: {error}</Body1>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={styles.root}>
        <Spinner
          size="tiny"
          label={warming ? "Waking up the database… 30–60s the first time after hours." : "Loading balances…"}
        />
      </div>
    );
  }

  if (data.balances.length === 0) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Subtitle2 as="h3" block>Balance · fiscal year {data.fiscalYear}</Subtitle2>
        <Caption1>days available, used, and pending approval</Caption1>
      </div>
      <div className={styles.tiles}>
        {data.balances.map((b) => (
          <div key={b.leaveTypeId} className={styles.tile}>
            <Body1 className={styles.typeName}>{b.leaveTypeName}</Body1>
            <span className={mergeClasses(styles.remaining, b.remaining < 0 && styles.remainingNegative)}>
              {formatDays(b.remaining)}
              <span className={styles.unit}>{Math.abs(b.remaining) === 1 ? "day" : "days"} left</span>
            </span>
            <Caption1 className={styles.breakdown}>
              Used {formatDays(b.used)} · Pending {formatDays(b.pending)} · Accrued {formatDays(b.accrued)}
            </Caption1>
          </div>
        ))}
      </div>
    </div>
  );
}
